<template>
  <button type="button"
          class="relative w-full cursor-pointer pl-6 pr-5 hover:bg-primary-light focus:outline-none focus:bg-primary-light border-none"
          :class="isSelected ? 'text-primary-focus bg-primary-light' : 'text-gray-dark bg-white'"
          @click="$emit('click')"
  >
    <div
      class="flex justify-between items-center py-5 border-t-0 border-l-0 border-r-0 border-b border-solid border-gray-a10"
      @click="handleFilterItem"
    >
      <div class="flex flex-col text-left">
        <span class="text-sm">{{ label }}</span>
        <div class="h-8 mt-2 text-base">
          <filter-value v-if="type === FILTER_ITEMS.BOT"
                        :label="values[FILTER_ITEMS.BOT].name"
                        :label-classes="'w-56 truncate'"
          >
            <bot-icon/>
          </filter-value>
          <date-from-filter-value v-else-if="type === FILTER_ITEMS.DATE_FROM" :date="values[FILTER_ITEMS.DATE_FROM]"/>
          <date-to-filter-value v-else-if="type === FILTER_ITEMS.DATE_TO" :date="values[FILTER_ITEMS.DATE_TO]"/>
          <filter-value v-if="type === FILTER_ITEMS.GRANULARITY"
                        :label="granularityLabel"
          >
            <granularity-icon/>
          </filter-value>

          <list-filter-value v-if="type === FILTER_ITEMS.REQUEST_OUTCOME"
                             :labels="outcomesLabels"
                             label-empty="No outcome selected"
                             label-full="All outcomes"
                             :options="outcomeOptions"
                             :hide-labels="hideOutcomeLabels"
                             class="gap-2"
          >
            <template v-if="checkedOutcomes.length !== outcomeOptions.length">
              <chart-legend v-for="outcome in checkedOutcomes"
                            :key="outcome"
                            :type="outcome"
                            tooltip-position="bottom"
                            :show-label="false"
                            class="h-4 w-4"
              />
            </template>
          </list-filter-value>

          <list-filter-value v-if="type === FILTER_ITEMS.ASSISTANCE_RESOLUTIONS"
                             :labels="resolutionLabels"
                             label-empty="No resolution selected"
                             label-full="All resolutions"
                             :options="resolutionOptions"
                             :hide-labels="hideResolutionLabels"
                             class="gap-2"
          >
            <template v-if="checkedResolutions.length !== resolutionOptions.length">
              <chart-legend v-for="resolution in checkedResolutions"
                            :key="resolution"
                            :type="resolution"
                            tooltip-position="bottom"
                            :show-label="false"
                            class="h-4 w-4"
              />
            </template>
          </list-filter-value>

          <list-filter-value v-if="type === FILTER_ITEMS.CHANNELS"
                             :labels="channelLabels"
                             label-empty="No channels selected"
                             label-full="All channels"
                             :options="channelOptions"
                             :hide-labels="hideChannelLabels"
                             :class="{ 'gap-2': selectedChannels.length !== channelOptions.length && selectedChannels.length }"
          >
            <template>
              <channels-icon v-if="!selectedChannels.length || selectedChannels.length === channelOptions.length"
                             class="mb-1 mr-2"
              />
              <channel-type-icons v-if="selectedChannels.length !== channelOptions.length"
                                  :selectedChannels="selectedChannels"
                                  :hasTooltip="hideChannelLabels"
              />
            </template>
          </list-filter-value>

          <filter-value v-if="type === FILTER_ITEMS.ANSWER_ID"
                        :label="answerProps.name"
          >
            <vs-icon icon="messages" size="1.2rem" class="mt-0.5"/>
          </filter-value>
        </div>
      </div>
      <vs-icon icon="keyboard_arrow_right" size="1.2rem"/>
    </div>

    <div v-if="isSelected"
         v-show="showItemDropdown"
         class="bg-white dropdown absolute z-10 rounded border border-solid border-gray-a10 shadow-lg">
      <radio-dropdown v-if="type === FILTER_ITEMS.BOT"
                      :options="botOptions"
                      :value="values[FILTER_ITEMS.BOT].id"
                      class="filter-menu-element w-max text-base"
                      @change="onValueChanged(FILTER_ITEMS.BOT, botOptions.find(({id}) => $event === id))"
      />

      <base-date-picker v-else-if="type === FILTER_ITEMS.DATE_FROM"
                        :clearable="false"
                        :value="values[FILTER_ITEMS.DATE_FROM]"
                        :showDaysForOtherMonth="false"
                        @change-date="onValueChanged(FILTER_ITEMS.DATE_FROM, $event)"
                        variant="dropdown"
                        class="test-date-picker-from filter-menu-element"
                        inline
      />

      <base-date-picker v-else-if="type === FILTER_ITEMS.DATE_TO"
                        :clearable="false"
                        :value="values[FILTER_ITEMS.DATE_TO]"
                        :showDaysForOtherMonth="false"
                        @change-date="onValueChanged(FILTER_ITEMS.DATE_TO, $event)"
                        variant="dropdown"
                        class="test-date-picker-to filter-menu-element "
                        inline
      />

      <radio-dropdown v-if="type === FILTER_ITEMS.GRANULARITY"
                      :options="GRANULARITY_OPTIONS"
                      :value="values[FILTER_ITEMS.GRANULARITY]"
                      class="filter-menu-element w-max text-base"
                      @change="onValueChanged(FILTER_ITEMS.GRANULARITY, $event)"
      />

      <checkbox-dropdown v-else-if="type === FILTER_ITEMS.REQUEST_OUTCOME"
                         :checked-values="checkedOutcomes"
                         :options="outcomeOptions"
                         :is-no-value-allowed="true"
                         class="filter-menu-element w-max text-base"
                         @change="onValueChanged(FILTER_ITEMS.REQUEST_OUTCOME, $event)"
      />

      <checkbox-dropdown v-else-if="type === FILTER_ITEMS.ASSISTANCE_RESOLUTIONS"
                         :checked-values="checkedResolutions"
                         :options="resolutionOptions"
                         :is-no-value-allowed="true"
                         class="filter-menu-element w-max text-base"
                         @change="onValueChanged(FILTER_ITEMS.ASSISTANCE_RESOLUTIONS, $event)"
      />

      <checkbox-dropdown v-else-if="type === FILTER_ITEMS.CHANNELS"
                         :checked-values="checkedChannels"
                         :options="channelOptions"
                         :is-no-value-allowed="true"
                         class="filter-menu-element w-max text-base"
                         @change="onValueChanged(FILTER_ITEMS.CHANNELS, $event)"
      />

      <answer-select v-else-if="type === FILTER_ITEMS.ANSWER_ID"
                     ref="answerSelect"
                     :answer-options="answerList"
                     select-variant="answerSelectFilters"
                     select-variant-hidden-dropdown="answerSelectFiltersHiddenDropdown"
                     class="w-64 text-base h-15 text-gray-dark"
                     :display-clear-button="true"
                     :intent-platform-id="answerProps.intentPlatformId"
                     placeholder="Search for an Answer..."
                     @change-answer="onValueChanged(FILTER_ITEMS.ANSWER_ID, getAnswerId($event.intentPlatformId))"
                     @remove-answer="handleRemoveAnswer"
      />
    </div>

  </button>
</template>

<script>
import ListFilterValue from './ListFilterValue'
import DateFromFilterValue from './DateFromFilterValue'
import DateToFilterValue from './DateToFilterValue'
import BaseDatePicker from '@/components/base-components/BaseDatePicker'
import CheckboxDropdown from '@/components/common/CheckboxDropdown'
import FilterValue from '@/components/analytics/filter/FilterValue'
import RadioDropdown from '@/components/common/RadioDropdown'
import ChartLegend from '@/components/analytics/charts/ChartLegend'
import ChannelsIcon from '@/assets/images/icons/ChannelsIcon'
import BotIcon from '@/assets/images/icons/BotIcon'
import GranularityIcon from '@/assets/images/icons/GranularityIcon'
import channelModule from '@/store/modules/channel.module'
import botModule from '@/store/modules/bot.module'
import outcomeModule from '@/store/modules/outcome.module'
import { FILTER_ITEMS, GRANULARITY_OPTIONS } from '@/utils/filter.constants'
import AnswerSelect from '@/components/analytics/conversations/detail/AnswerSelect.vue'
import answerModule from '@/store/modules/answer.module'
import ChannelTypeIcons from '@/components/analytics/filter/ChannelTypeIcons.vue'

export default {
  components: {
    AnswerSelect,
    ChannelsIcon,
    FilterValue,
    ListFilterValue,
    DateFromFilterValue,
    DateToFilterValue,
    BaseDatePicker,
    CheckboxDropdown,
    RadioDropdown,
    ChartLegend,
    BotIcon,
    GranularityIcon,
    ChannelTypeIcons
  },
  name: 'FilterMenuItem',
  props: {
    label: {
      type: String,
      required: true
    },
    isSelected: {
      type: Boolean,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    values: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      FILTER_ITEMS,
      GRANULARITY_OPTIONS,
      showItemDropdown: true
    }
  },

  computed: {
    channelOptions: channelModule.getChannelList,
    botOptions: botModule.getBotList,
    outcomeOptions: outcomeModule.getOutcomeList,
    resolutionOptions: outcomeModule.getResolutionList,
    answerList: answerModule.getAnswerList,

    channelLabels() {
      return this.values[FILTER_ITEMS.CHANNELS] === null ?
        this.channelOptions.map(({ name }) => name) :
        this.selectedChannels.map(({ name }) => name)
    },

    selectedChannels() {
      if (!this.values[FILTER_ITEMS.CHANNELS]) return []

      return this.channelOptions.filter(
        ({ id }) => this.values[FILTER_ITEMS.CHANNELS].includes(id)
      )
    },

    checkedChannels() {
      return this.values[FILTER_ITEMS.CHANNELS] === null ?
        this.channelOptions.map(({ id }) => id) :
        this.values[FILTER_ITEMS.CHANNELS]
    },

    outcomesLabels() {
      const selectedOptions = this.outcomeOptions.filter(
        ({ id }) => this.values[FILTER_ITEMS.REQUEST_OUTCOME]?.includes(id)
      )
      return this.values[FILTER_ITEMS.REQUEST_OUTCOME] === null ?
        this.outcomeOptions.map(({ name }) => name) :
        selectedOptions.map(({ name }) => name)
    },

    resolutionLabels() {
      const selectedOptions = this.resolutionOptions.filter(
        ({ id }) => this.values[FILTER_ITEMS.ASSISTANCE_RESOLUTIONS]?.includes(id)
      )
      return this.values[FILTER_ITEMS.ASSISTANCE_RESOLUTIONS] === null ?
        this.resolutionOptions.map(({ name }) => name) :
        selectedOptions.map(({ name }) => name)
    },

    checkedOutcomes() {
      const outcomeValues = this.values[FILTER_ITEMS.REQUEST_OUTCOME]
      return outcomeValues === null ?
        this.outcomeOptions.map(({ id }) => id) :
        outcomeValues
    },

    checkedResolutions() {
      const resolutionValues = this.values[FILTER_ITEMS.ASSISTANCE_RESOLUTIONS]
      return resolutionValues === null ?
        this.resolutionOptions.map(({ id }) => id) :
        resolutionValues
    },

    granularityLabel() {
      const { name } = GRANULARITY_OPTIONS.find(({ id }) => id === this.values[FILTER_ITEMS.GRANULARITY])
      return name
    },

    answerProps() {
      const answerId = this.values[FILTER_ITEMS.ANSWER_ID]
      if (!answerId) return { name: 'All answers', intentPlatformId: null }

      const { name, intent } = this.answerList.find(({ id }) => id === this.values[FILTER_ITEMS.ANSWER_ID])
      return { name, intentPlatformId: intent.platformId }
    },

    hideOutcomeLabels() {
      return this.checkedOutcomes.length > 1 && this.checkedOutcomes.length !== this.outcomeOptions.length
    },

    hideResolutionLabels() {
      return this.checkedResolutions.length > 1 && this.checkedResolutions.length !== this.resolutionOptions.length
    },

    hideChannelLabels() {
      return this.selectedChannels.length > 1 && this.selectedChannels.length !== this.channelOptions.length
    }
  },

  methods: {
    onValueChanged(type, value) {
      this.$emit('change', { [type]: value })
    },

    getAnswerId(intentPlatformId) {
      return this.answerList.find(({ intent }) => intent.platformId === intentPlatformId).id
    },

    handleRemoveAnswer() {
      this.onValueChanged(FILTER_ITEMS.ANSWER_ID, null)
      this.showItemDropdown = false
    },

    async handleFilterItem() {
      this.showItemDropdown = true
      if (this.type === FILTER_ITEMS.ANSWER_ID) {
        // delay to get dropdown mounted
        await this.$nextTick()
        this.$refs.answerSelect?.focusOnAnswerSelect()
      }
    }
  }
}
</script>

<style scoped>
.dropdown {
  top: 1.25rem;
  left: 19.4rem;
}
</style>
